'use client';

import {
  cloneElement,
  useCallback,
  useMemo,
  useState,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Slot } from '@radix-ui/react-slot';
import { useIsBreakpoint } from '../../../hooks/use-is-breakpoint';
import { cn } from '../../../lib/utils';
import { DrawerMenu, DrawerMenuContent, type DrawerMenuProps } from '../drawer-menu/drawer-menu';
import { DrawerMenuHeader } from '../drawer-menu/drawer-menu-header';
import {
  PopoverMenu,
  PopoverMenuContent,
  PopoverMenuTrigger,
  type PopoverMenuContentProps,
} from '../popover-menu/popover-menu';
import {
  DrawerOrPopoverMenuProvider,
  useDrawerOrPopoverMenu,
  type DrawerOrPopoverMenuContextValue,
} from './drawer-or-popover-menu-context';

export type DrawerOrPopoverMenuProps = {
  children?: ReactNode;
  /** Controlled open state. Leave it out and the menu keeps its own. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Override the breakpoint-based choice: `true` renders the popover, `false` the drawer.
   * When omitted, uses `useIsBreakpoint('--breakpoint-md')` (768px).
   */
  isAboveBreakpoint?: boolean;
};

/**
 * One menu tree rendered as a `PopoverMenu` above the breakpoint and as a `DrawerMenu`
 * below it. Meant for menus that read the same in both, with the trigger and the rows
 * composed from the parts of this family. A menu that needs the two to differ, or a
 * feature only one of them has, uses `PopoverMenu` and `DrawerMenu` directly.
 */
export const DrawerOrPopoverMenu = ({
  children,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  isAboveBreakpoint: isAboveBreakpointProp,
}: DrawerOrPopoverMenuProps) => {
  const isAboveBreakpointDefault = useIsBreakpoint('--breakpoint-md');
  const isAboveBreakpoint = isAboveBreakpointProp ?? isAboveBreakpointDefault;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = openProp ?? uncontrolledOpen;

  // the uncontrolled state is kept either way, the prop simply wins when given
  const setOpen = useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const contextValue = useMemo<DrawerOrPopoverMenuContextValue>(
    () => ({ isAboveBreakpoint, open, setOpen }),
    [isAboveBreakpoint, open, setOpen],
  );

  return (
    <DrawerOrPopoverMenuProvider value={contextValue}>
      {isAboveBreakpoint ? (
        <PopoverMenu open={open} onOpenChange={(next) => setOpen(next)}>
          {children}
        </PopoverMenu>
      ) : (
        // the sheet itself is rendered by the content, which holds its header and rows
        children
      )}
    </DrawerOrPopoverMenuProvider>
  );
};

export type DrawerOrPopoverMenuTriggerProps = Omit<
  ComponentPropsWithoutRef<'button'>,
  'children'
> & {
  children?: ReactNode;
  /** Element to render as, e.g. an `IconButton`; `children` go inside it. */
  render?: ReactElement;
};

/** The element that opens the menu: a Base UI trigger for the popover, a button for the drawer. */
export const DrawerOrPopoverMenuTrigger = ({
  render,
  children,
  onClick,
  ...props
}: DrawerOrPopoverMenuTriggerProps) => {
  const { isAboveBreakpoint, open, setOpen } = useDrawerOrPopoverMenu();

  if (isAboveBreakpoint) {
    return (
      <PopoverMenuTrigger render={render} onClick={onClick} {...props}>
        {children}
      </PopoverMenuTrigger>
    );
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    setOpen(true);
  };

  const Comp = render ? Slot : 'button';

  return (
    <Comp
      type={render ? undefined : 'button'}
      aria-haspopup="dialog"
      aria-expanded={open}
      data-slot="drawer-or-popover-menu-trigger"
      onClick={handleClick}
      {...props}>
      {render && children !== undefined
        ? cloneElement(render, undefined, children)
        : (render ?? children)}
    </Comp>
  );
};

export type DrawerOrPopoverMenuContentProps = {
  children?: ReactNode;
  /** Applied to the popover popup and to the drawer's root panel. */
  className?: string;
  /** Title of the drawer header, which is the sheet's accessible name. The popover has none. */
  title: ReactNode;
  /**
   * Shows the drawer title. When `false` it is only visually hidden: `title` is still
   * required, as it stays the sheet's accessible name.
   */
  showTitle?: boolean;
  /** Props forwarded only to the popover rendering */
  popoverProps?: Pick<
    PopoverMenuContentProps,
    'side' | 'align' | 'sideOffset' | 'alignOffset' | 'className'
  >;
  /** Props forwarded only to the drawer rendering */
  drawerProps?: Pick<DrawerMenuProps, 'onCloseComplete'> & { className?: string };
};

/**
 * The rows. Above the breakpoint it is the popover popup; below it, the whole sheet:
 * the standard drawer header with `title`, then the rows.
 */
export const DrawerOrPopoverMenuContent = ({
  children,
  className,
  title,
  showTitle,
  popoverProps,
  drawerProps,
}: DrawerOrPopoverMenuContentProps) => {
  const { isAboveBreakpoint, open, setOpen } = useDrawerOrPopoverMenu();

  if (isAboveBreakpoint) {
    return (
      <PopoverMenuContent {...popoverProps} className={cn(className, popoverProps?.className)}>
        {children}
      </PopoverMenuContent>
    );
  }

  return (
    <DrawerMenu
      open={open}
      onOpenChange={(next) => setOpen(next)}
      onCloseComplete={drawerProps?.onCloseComplete}>
      <DrawerMenuHeader title={title} showTitle={showTitle} />
      <DrawerMenuContent className={cn(className, drawerProps?.className)}>
        {children}
      </DrawerMenuContent>
    </DrawerMenu>
  );
};
